import { useState, useEffect, useRef, memo } from 'react';
import cn from 'classnames';
import Slider from 'react-slick';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { pushEvent } from '../../../utilities/analytics';
import {
  HOTEL_GALLERY_CLICKED_EVENT,
  RESULT_CARD_MESSAGE_DISPLAYED,
  RESULT_CARD_SWIPED,
} from '../../../constants/analyticEvents';
import abTesting from '../../../services/abTesting';
import { useGetSliderTutorialMessagesQuery } from '../rtkSliderTutorialMessages';

const DealSlider = ({
  photos,
  index,
  dealItem,
  setTutorialVisible,
}) => {
  const sliderRef = useRef(null);
  const [slideCount, setSlideCount] = useState(0);
  const photosToShow = photos.slice(0, 5);
  const [cachedIndices, setCachedIndices] = useState([0]);
  const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showTutorialOverlay, setShowTutorialOverlay] = useState(false);
  const { data, isError, isLoading } = useGetSliderTutorialMessagesQuery({ skip: !abTesting.showCardsTutorialMsgs });
  let message;
  if (!isLoading && !isError && data?.length > 0) {
    message = data[index % data.length];
  }

  const customDotsLength = abTesting.showCardsTutorialMsgs ? photosToShow.length + 1 : photosToShow.length;

  useEffect(() => {
    !cachedIndices.includes(slideCount) && setCachedIndices([...cachedIndices, slideCount]);
  }, [cachedIndices, slideCount]);

  const NextArrow = ({ onClick, currentSlide }) => {
    const onNextClick = (e) => {
      e.stopPropagation();
      if (abTesting.showCardsTutorialMsgs) {
        if (currentSlide === photosToShow.length - 1 && !showTutorialOverlay) {
          setShowTutorialOverlay(true);
          setTutorialVisible(true);
          pushEvent(RESULT_CARD_MESSAGE_DISPLAYED, { message: message?.text });
        } else {
          setSlideCount(currentSlide + 1);
          onClick();
          setShowTutorialOverlay(false);
          setTutorialVisible(false);
        }
      } else {
        setSlideCount(currentSlide + 1);
        onClick();
      }
    }

    return (
      <button
        onTouchStart={(e) => e.stopPropagation()}
        className="photo__button next-button photo__button-active"
        onClick={onNextClick}
      >
        <i className="ri-arrow-right-s-line photo__button-arrow" />
      </button>
    );
  };

  const PrevArrow = ({ onClick, currentSlide }) => {
    const onPrevClick = (e) => {
      e.stopPropagation();
      if (abTesting.showCardsTutorialMsgs) {
        if (currentSlide === photosToShow.length - 1 && showTutorialOverlay) {
          setShowTutorialOverlay(false);
          setTutorialVisible(false);
        } else if (currentSlide === 0) {
          setSlideCount(photosToShow.length);
          setShowTutorialOverlay(true);
          setTutorialVisible(true);
          onClick();
        } else {
          setSlideCount(currentSlide - 1);
          onClick();
        }
      } else {
        setSlideCount(currentSlide - 1);
        onClick();
      }
    }

    return (
      <button
        onTouchStart={(e) => e.stopPropagation()}
        className="photo__button prev-button photo__button-active"
        onClick={onPrevClick}
      >
        <i className="ri-arrow-left-s-line photo__button-arrow" />
      </button>
    );
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (old, next) => {
      setSlideCount(next);
      setCurrentIndex(next);
      pushEvent(RESULT_CARD_SWIPED, {
        imageIndex: next,
        searchParams: JSON.parse(sessionStorage.getItem('hfLastFilters')),
        dealItem,
      })
      pushEvent(HOTEL_GALLERY_CLICKED_EVENT);
    },
    className: 'deal-slider',
  };

  return (
    <>
      <Slider {...settings} ref={sliderRef}>
        {photosToShow.map((photoUrl, index) => {
          const content = (
            <div className={`photo__wrap`} key={photoUrl}>
              <LazyLoadImage
                src={photoUrl}
                alt="hotel"
                onLoad={
                  index === 0
                    ? () => {
                        setIsFirstImageLoaded(true);
                      }
                    : undefined
                }
                className='photo'
                useIntersectionObserver
              />
              <div className="photo__shadow" />
              {showTutorialOverlay && index === photosToShow.length - 1 && (
                <div className="deal-slider-tutorial">
                  <div className="tutorial-content">
                    <i className={`ri-${message?.icon}` || 'ri-calendar-line'} />
                    <span>{message?.text}</span>
                  </div>
                </div>
              )}
            </div>
          );

          const empty = (
            <div className={`photo__wrap`} key={photoUrl}>
              {' '}
            </div>
          );

          if (index === 0 && !isFirstImageLoaded) {
            return content;
          }
          if (
            (slideCount - 1 === index ||
              slideCount + 1 === index ||
              slideCount === index ||
              index === photosToShow.length - 1 ||
              cachedIndices.includes(index)) &&
            isFirstImageLoaded
          ) {
            return content;
          }
          return empty;
        })}
      </Slider>
      <ul className={cn('slick-dots', { 'slick-dots-shifted': abTesting.showCardsTutorialMsgs })}>
        {Array.from({ length: customDotsLength }, (_, index) => (
          <li key={index} className={cn({ 'slick-active': (index === currentIndex && !showTutorialOverlay) || (index === photosToShow.length && showTutorialOverlay) })}><button></button></li>
        ))}
      </ul>
    </>
  );
}

export default memo(DealSlider);
